import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '../../utils/errorUtils';
import { formatMoney } from '../reports/formatters';
import { useGetOptionsQuery, useLazyGenerateReportQuery } from './api/momSalesReportApi';
import { exportMoMSalesReport } from './exportMomSalesReport';
import type { MoMSalesReportClientOption, MoMSalesReportRequest, MoMSalesReportRow } from './types';
import { formatVariance, getDefaultBasePeriod, getVarianceColor } from './utils';

const createRequest = (
  reportingPeriod: string,
  basePeriod: string,
  clientIds: number[]
): MoMSalesReportRequest => ({
  ReportingPeriod: reportingPeriod,
  BasePeriod: basePeriod,
  ClientIds: clientIds,
});

const ReportRow = ({ row }: { row: MoMSalesReportRow }) => {
  const varianceColor = getVarianceColor(row.varianceUsd);

  return (
    <TableRow
      sx={
        row.isTotal ? { backgroundColor: 'action.hover', '& > *': { fontWeight: 700 } } : undefined
      }
    >
      <TableCell>{row.clientName}</TableCell>
      <TableCell align="right">{formatMoney(row.basePeriodSalesUsd)}</TableCell>
      <TableCell align="right">{formatMoney(row.reportingPeriodSalesUsd)}</TableCell>
      <TableCell align="right" sx={{ color: varianceColor }}>
        {formatVariance(row.varianceUsd)}
      </TableCell>
      <TableCell align="right" sx={{ color: varianceColor }}>
        {row.variancePercentDisplay}
      </TableCell>
    </TableRow>
  );
};

export const MoMSalesReportPage = () => {
  const { data: options, isLoading: optionsLoading, error: optionsError } = useGetOptionsQuery();
  const [
    generateReport,
    { data: report, error: reportRequestError, isFetching: reportLoading, reset: resetReport },
  ] = useLazyGenerateReportQuery();
  const [reportingPeriod, setReportingPeriod] = useState<string | null>(null);
  const [basePeriod, setBasePeriod] = useState<string | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filtersCleared, setFiltersCleared] = useState(false);
  const initialized = useRef(false);

  const periods = options?.periods ?? [];
  const clients = useMemo(() => options?.clients ?? [], [options?.clients]);
  const defaultReportingPeriod = periods[0]?.value ?? null;
  const defaultBasePeriod = getDefaultBasePeriod(periods, defaultReportingPeriod);
  const activeReportingPeriod = reportingPeriod ?? (filtersCleared ? null : defaultReportingPeriod);
  const activeBasePeriod = basePeriod ?? (filtersCleared ? null : defaultBasePeriod);
  const selectedClients = useMemo(
    () => clients.filter((client) => selectedClientIds.includes(client.id)),
    [clients, selectedClientIds]
  );

  useEffect(() => {
    if (initialized.current || !defaultReportingPeriod || !defaultBasePeriod) return;

    initialized.current = true;
    void generateReport(createRequest(defaultReportingPeriod, defaultBasePeriod, []));
  }, [defaultBasePeriod, defaultReportingPeriod, generateReport]);

  const handleGenerate = () => {
    if (!activeReportingPeriod || !activeBasePeriod) {
      setError('Reporting Period and Base Period are required.');
      return;
    }

    setError(null);
    void generateReport(createRequest(activeReportingPeriod, activeBasePeriod, selectedClientIds));
  };

  const handleReportingPeriodChange = (value: string | null) => {
    const currentBasePeriod = activeBasePeriod;
    setFiltersCleared(true);
    setReportingPeriod(value);
    setBasePeriod(
      value && value === currentBasePeriod
        ? getDefaultBasePeriod(periods, value)
        : currentBasePeriod
    );
  };

  const handleBasePeriodChange = (value: string | null) => {
    setFiltersCleared(true);
    setReportingPeriod(activeReportingPeriod);
    setBasePeriod(value);
  };

  const handleClear = () => {
    setReportingPeriod(null);
    setBasePeriod(null);
    setSelectedClientIds([]);
    setError(null);
    setFiltersCleared(true);
    resetReport();
  };

  const handleExport = async () => {
    if (!report?.rows.length) return;

    try {
      await exportMoMSalesReport(report);
    } catch (exportError) {
      setError(extractErrorMessage(exportError, 'Failed to export MoM sales report.'));
    }
  };

  const selectedReportingPeriod = periods.find((period) => period.value === activeReportingPeriod);
  const selectedBasePeriod = periods.find((period) => period.value === activeBasePeriod);
  const basePeriodOptions = periods.filter((period) => period.value !== activeReportingPeriod);
  const reportingPeriodOptions = periods.filter((period) => period.value !== activeBasePeriod);
  const displayedRows = report?.rows ?? [];
  const isLoading = optionsLoading || reportLoading;

  return (
    <Box
      component="section"
      aria-label="MoM Sales Report"
      sx={{ p: { xs: 1, md: 2 }, height: '100%', overflow: 'auto' }}
    >
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <Autocomplete
            fullWidth
            options={reportingPeriodOptions}
            value={selectedReportingPeriod ?? null}
            onChange={(_event, value) => handleReportingPeriodChange(value?.value ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={isLoading}
            renderInput={(params) => <TextField {...params} label="Reporting Period" />}
          />
          <Autocomplete
            fullWidth
            options={basePeriodOptions}
            value={selectedBasePeriod ?? null}
            onChange={(_event, value) => handleBasePeriodChange(value?.value ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={isLoading}
            renderInput={(params) => <TextField {...params} label="Base Period" />}
          />
          <Autocomplete<MoMSalesReportClientOption, true>
            multiple
            fullWidth
            options={clients}
            value={selectedClients}
            onChange={(_event, value) => setSelectedClientIds(value.map((client) => client.id))}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            disabled={isLoading}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Clients" placeholder="All clients" />
            )}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, height: 42 }}>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isLoading}
              sx={{ height: '100%', whiteSpace: 'nowrap' }}
            >
              {reportLoading ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={isLoading}
              sx={{ height: '100%' }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {(optionsError || reportRequestError || error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ||
            extractErrorMessage(
              reportRequestError || optionsError,
              'Failed to load MoM sales report.'
            )}
        </Alert>
      )}

      {isLoading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {report && (
        <Paper variant="outlined">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {selectedBasePeriod?.label || report.basePeriodCaption} /{' '}
              {selectedReportingPeriod?.label || report.reportingPeriodCaption}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => void handleExport()}
              disabled={displayedRows.length === 0}
            >
              Export to Excel
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">{report.basePeriodCaption} (Base Period)</TableCell>
                  <TableCell align="right">
                    {report.reportingPeriodCaption} (Reporting Period)
                  </TableCell>
                  <TableCell align="right">Variance, USD</TableCell>
                  <TableCell align="right">Variance, %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRows.length > 0 ? (
                  displayedRows.map((row) => <ReportRow key={row.id} row={row} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
